import { ServiceBoxConfig } from '@/components/ServiceBoxes/helpers/serviceboxes-helpers';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { Category, Country } from '@smg-real-estate/hg-rets-schema';

/**
 * Checks if a listing is eligible for Internet Service Provider (ISP) Sunrise integration:
 * - property must be in Switzerland (country is CH) AND
 * - none of its categories may be PARKING_SPACE or GARAGE.
 *
 * @param country - listing country (must be CH)
 * @param categories - listing categories (must not include PARKING_SPACE or GARAGE)
 * @returns boolean - true if listing meets Sunrise eligibility criteria, false otherwise
 */
export function isListingEligibleForISP(country: Country, categories: Category[]): boolean {
  return (
    country === Country.Ch &&
    !categories.some(
      (category) => category === Category.ParkingSpace || category === Category.Garage,
    )
  );
}

/**
 * Generates a Sunrise ISP partner landing page URL with optional query parameters.
 *
 * Optionally appends UTM or other analytics parameters to inform the ISP partner
 * about the traffic source and marketing context for deeper attribution.
 *
 * @param baseUrl - Sunrise landing page URL
 * @param queryParameters - An optional object of key-value pairs (for example, UTM parameters)
 *   to be URL-encoded and appended as a query string to the Sunrise landing page URL.
 *   Example: \{ utm_source: 'dis_performance', utm_medium: 'service_box_homegate' \}
 * @returns The formatted Sunrise landing page URL, including any provided query parameters.
 */
export function buildSunriseLink(
  baseUrl: string,
  queryParameters?: Record<string, string>,
): string {
  if (queryParameters && Object.keys(queryParameters).length > 0) {
    // Filter out parameters with empty, null, or undefined values
    const validParameters = Object.entries(queryParameters).reduce(
      (acc, [key, value]) => {
        if (value && value.trim() !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    if (Object.keys(validParameters).length > 0) {
      const qs = new URLSearchParams(validParameters).toString();
      return `${baseUrl}?${qs}`;
    }
  }
  return baseUrl;
}

// Sunrise base URLs by locale
export const SUNRISE_BASE_URLS: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: 'https://www.sunrise.ch/de/home',
  [HgSupportedLanguage.EN]: 'https://www.sunrise.ch/en/home',
  [HgSupportedLanguage.FR]: 'https://www.sunrise.ch/fr/home',
  [HgSupportedLanguage.IT]: 'https://www.sunrise.ch/it/home',
};

// Sunrise ISP ID
export const SUNRISE_ISP_ID = 'sunrise';

/**
 * Creates a dynamic Sunrise ISP service box link with UTM parameters for tracking.
 
 * If the Sunrise box exists within the current service box array, this returns an updated ServiceBoxConfig
 * with the constructed external URL, ready for injection into the UI.
 *
 * @param originalServiceBox - The Sunrise service box configuration to modify
 * @param serviceBoxes - The current array of service box configs for the page
 * @param locale - The user's locale for generating a localized Sunrise landing page link
 * @returns An object with the Sunrise service box's index and a new ServiceBoxConfig with the updated link,
 *          or undefined if the Sunrise box is not present.
 */
export function handleISPServiceBoxDynamicLink(
  originalServiceBox: ServiceBoxConfig,
  serviceBoxes: any[],
  locale: HgSupportedLanguage,
):
  | {
      serviceBoxIndex: number;
      serviceBoxWithDynamicLink: ServiceBoxConfig;
    }
  | undefined {
  const serviceBoxIndex = serviceBoxes.findIndex((serviceBox) => serviceBox.id === SUNRISE_ISP_ID);
  if (serviceBoxIndex !== -1) {
    const sunriseBaseUrl = SUNRISE_BASE_URLS[locale];
    const sunriseLinkWithUTM = buildSunriseLink(sunriseBaseUrl, {
      // TODO: Will be handled in KK-1725. E.g.
      // utm_source: 'dis_performance',
      // utm_medium: 'service_box_homegate',
      // utm_campaign: 'isp_integration',
    });

    return {
      serviceBoxWithDynamicLink: {
        ...originalServiceBox,
        link: {
          text: originalServiceBox.link.text,
          href: sunriseLinkWithUTM,
          hrefSkipTranslation: true, // Already a full URL with params
        },
      },
      serviceBoxIndex,
    };
  }
  return undefined;
}
